import * as React from "react";
import * as ToastPrimitives from "@radix-ui/react-toast";
import { cva, type VariantProps } from "class-variance-authority";
import { X, CheckCircle2, AlertCircle, Info, AlertTriangle } from "lucide-react";
import { cn } from "@/lib/utils";

const ToastProvider = ToastPrimitives.Provider;

const ToastViewport = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Viewport>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Viewport>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Viewport
    ref={ref}
    className={cn(
      "fixed bottom-0 right-0 z-[100] flex max-h-screen w-full flex-col gap-2 p-4 md:max-w-[420px]",
      className,
    )}
    {...props}
  />
));
ToastViewport.displayName = ToastPrimitives.Viewport.displayName;

const toastVariants = cva(
  "group pointer-events-auto relative flex w-full items-start gap-3 overflow-hidden rounded-2xl border p-4 shadow-lg backdrop-blur-xl transition-all data-[swipe=cancel]:translate-x-0 data-[swipe=end]:translate-x-[var(--radix-toast-swipe-end-x)] data-[swipe=move]:translate-x-[var(--radix-toast-swipe-move-x)] data-[swipe=move]:transition-none data-[state=open]:animate-toast-slide-in data-[state=closed]:animate-toast-slide-out",
  {
    variants: {
      variant: {
        default: "border-border/50 bg-background/95 text-foreground",
        success: "border-emerald-500/30 bg-emerald-500/10 text-foreground",
        destructive: "border-destructive/30 bg-destructive/10 text-foreground",
        warning: "border-amber-500/30 bg-amber-500/10 text-foreground",
        info: "border-blue-500/30 bg-blue-500/10 text-foreground",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

const iconMap = {
  default: null,
  success: CheckCircle2,
  destructive: AlertCircle,
  warning: AlertTriangle,
  info: Info,
};

const iconColorMap = {
  default: "text-foreground",
  success: "text-emerald-500",
  destructive: "text-destructive",
  warning: "text-amber-500",
  info: "text-blue-500",
};

const progressColorMap = {
  default: "bg-primary",
  success: "bg-emerald-500",
  destructive: "bg-destructive",
  warning: "bg-amber-500",
  info: "bg-blue-500",
};

interface CustomToastProps extends React.ComponentPropsWithoutRef<typeof ToastPrimitives.Root>,
  VariantProps<typeof toastVariants> {
  duration?: number;
}

const CustomToast = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Root>,
  CustomToastProps
>(({ className, variant = "default", duration = 5000, children, ...props }, ref) => {
  const Icon = iconMap[variant || "default"];
  const iconColor = iconColorMap[variant || "default"];
  const progressColor = progressColorMap[variant || "default"];

  return (
    <ToastPrimitives.Root 
      ref={ref} 
      className={cn(toastVariants({ variant }), className)} 
      duration={duration}
      {...props}
    >
      {Icon && (
        <div className={cn("shrink-0 mt-0.5", iconColor)}>
          <Icon className="h-5 w-5" />
        </div>
      )}
      <div className="flex-1 min-w-0">
        {children}
      </div>
      <ToastPrimitives.Close className="shrink-0 rounded-full p-1 text-foreground/50 opacity-70 transition-opacity hover:opacity-100 hover:bg-foreground/10 focus:outline-none focus:ring-2 focus:ring-ring">
        <X className="h-4 w-4" />
      </ToastPrimitives.Close>
      
      {/* Progress bar */}
      <div className="absolute bottom-0 left-0 right-0 h-1 overflow-hidden rounded-b-2xl bg-foreground/5">
        <div 
          className={cn("h-full animate-toast-progress origin-left", progressColor)}
          style={{ 
            animationDuration: `${duration}ms`,
          }}
        />
      </div>
    </ToastPrimitives.Root>
  );
});
CustomToast.displayName = "CustomToast";

const ToastTitle = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Title>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Title>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Title 
    ref={ref} 
    className={cn("text-sm font-semibold leading-tight", className)} 
    {...props} 
  />
));
ToastTitle.displayName = ToastPrimitives.Title.displayName;

const ToastDescription = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Description>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Description>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Description 
    ref={ref} 
    className={cn("text-sm text-muted-foreground mt-1", className)} 
    {...props} 
  />
));
ToastDescription.displayName = ToastPrimitives.Description.displayName;

const ToastAction = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Action>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Action>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Action
    ref={ref}
    className={cn(
      "inline-flex h-8 shrink-0 items-center justify-center rounded-full border bg-transparent px-3 text-sm font-medium transition-colors hover:bg-secondary focus:outline-none focus:ring-2 focus:ring-ring disabled:pointer-events-none disabled:opacity-50",
      className,
    )}
    {...props}
  />
));
ToastAction.displayName = ToastPrimitives.Action.displayName;

export {
  ToastProvider,
  ToastViewport,
  CustomToast,
  ToastTitle,
  ToastDescription,
  ToastAction,
  type CustomToastProps,
};
